import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { 
  Beaker, 
  Shield, 
  Sparkles, 
  Archive, 
  ArrowRight, 
  CheckCircle2,
  Zap,
  Users,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Beaker: Beaker,
  Shield: Shield,
  Sparkles: Sparkles,
  Archive: Archive,
};

import { categories } from "../data/categories";

const HomePage = () => {
  //const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    if (category.available) {
      navigate(`/questionnaire/${category.id}`);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="relative bg-[#0B1D2B] py-16 md:py-24 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300BCD4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold text-white tracking-tight uppercase mb-6 animate-fadeIn">
              AXEON Chemical
              <span className="block text-[#00BCD4]">Product Selection Tool</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed animate-fadeIn stagger-1">
              Find the perfect chemical solution for your reverse osmosis or nanofiltration system. 
              Answer a few questions and get expert recommendations tailored to your application
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeIn stagger-2">
              <Link to="/products">
                <Button 
                  className="bg-[#00BCD4] hover:bg-[#00acc1] text-white font-bold py-6 px-6 rounded shadow-lg hover:shadow-xl transition-all uppercase tracking-wide"
                  data-testid="view-all-products-btn"
                >
                  View All Products
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/calculator">
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold py-6 px-6 rounded transition-all uppercase tracking-wide"
                  data-testid="calculator-btn"
                >
                  Dosage Calculator
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Category Selection */}
      <section className="py-16 md:py-20" data-testid="category-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 tracking-tight mb-4">
              Select a Chemical Category
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choose the type of chemical solution you need. Our guided questionnaire will help you 
              find the perfect product for your specific application.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" data-testid="loading-spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category, index) => {
                const IconComponent = iconMap[category.icon] || Beaker;
                return (
                  <div
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className={`category-card ${!category.available ? 'disabled' : ''} animate-fadeIn`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`category-card-${category.id}`}
                  >
                    {/* Image */}
                    <div className="h-80 overflow-hidden">
                      <img 
                        src={category.image} 
                        alt={category.name}
                        className="w-full px-5 pt-5 h-auto object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="p-6 pt-0">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          category.available ? 'bg-[#00BCD4]' : 'bg-slate-300'
                        }`}>
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800">
                          {category.name}
                        </h3>
                      </div>
                      <p className="text-slate-600 text-sm mb-4">
                        {category.description}
                      </p>
                      {category.available ? (
                        <span className="inline-flex items-center text-[#00BCD4] font-medium text-sm">
                          Start Selection <ArrowRight className="w-4 h-4 ml-1" />
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm font-medium">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* <pre>{JSON.stringify(categories, null, 2)}</pre> */}
            </div>
          )}
        </div>
      </section>

      {/* Need Help Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="help-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 tracking-tight mb-4">
              Need Help Choosing?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our intelligent selection tool guides you through a simple questionnaire to find 
              the perfect chemical solution for your specific needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: CheckCircle2,
                title: "Expert Recommendations",
                description: "Get product suggestions based on proven engineering criteria and industry best practices."
              },
              {
                icon: Zap,
                title: "Fast & Easy",
                description: "Answer a few simple questions and receive instant recommendations in under 2 minutes."
              },
              {
                icon: Users,
                title: "Tailored Solutions",
                description: "Every recommendation is customized to your specific water source, application, and system requirements."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="text-center p-8 rounded-xl bg-slate-50 border border-slate-100 card-hover"
              >
                <div className="w-14 h-14 rounded-full bg-[#00BCD4]/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-[#00BCD4]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
